import React, { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { Beat } from "../../plugins/spinners.plugin";
import Button from "../buttons/buttons";
import { ReactComponent as Close } from "../../assets/images/icons/close.svg";
import { ReactComponent as CategoryIcon } from "../../assets/images/icons/category-red.svg";
import { ReactComponent as TagIcon } from "../../assets/images/icons/tag-grey.svg";
import { getAllTags } from "../../redux/productTags/productTagActions";

export function FilterModal(props) {
  const dispatch = useDispatch();
  const params = useParams();
  const [tags, setTags] = useState([]);
  const [activeTab, setActiveTab] = useState("categories");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [data, setData] = useState({
    restaurant_categories: [],
    restaurant_tags: [],
  });
  const { restaurant_categories, restaurant_tags } = data;
  const { allTags } = useSelector((state) => state.productTag);
  const { isLoading } = useSelector((state) => state.publicRestaurant);

  useEffect(() => {
    dispatch(getAllTags());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let items = allTags?.data?.filter((item) => item.type === "restaurant");
    setTags(items);
  }, [allTags]);

  const handleTagSelect = (tag) => {
    const isSameTag = selectedTag === tag.name;
    setSelectedTag(isSameTag ? null : tag.name);
    setData({
      restaurant_tags: isSameTag ? [] : [tag.name],
      restaurant_categories: [],
    });
    setSelectedCategory(null);
  };

  const handleCategorySelect = (category) => {
    const isSameCategory = selectedCategory === category.name;
    setSelectedCategory(isSameCategory ? null : category.name);
    setData({
      restaurant_categories: isSameCategory ? [] : [category.name],
      restaurant_tags: [],
    });
    setSelectedTag(null);
  };

  const handleClear = () => {
    setData({
      restaurant_tags: [],
      restaurant_categories: [],
    });
    setSelectedTag(null);
    setSelectedCategory(null);
    setActiveTab("tags");
    props.setFilterCount(0);
    if (props.onClear) {
      props.onClear();
    }
    props.onClose();
  };

  return (
    <div data-test="filterModal">
      <Transition show={props.show} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={props.onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center sm:items-center justify-center min-h-full p-4 sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-[650px] bg-white dark:bg-[#121212] rounded-3xl px-4 pt-5 pb-4  overflow-hidden shadow-xl transform transition-all sm:my-10 sm:max-w-lg sm:w-full  sm:p-8">
                  <div className=" sm:block absolute top-1 sm:top-2 right-5 pt-4">
                    <button
                      type="button"
                      className="bg-[#ffeae5] dark:bg-[#CE2600] rounded-md sm:p-3 p-2"
                      onClick={props.onClose}
                    >
                      <span className="sr-only">Close</span>
                      <Close className="w-3 h-3 sm:w-4 sm:h-4 white-icon" />
                    </button>
                  </div>
                  <div className="text-left sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-xl mb-2 font-bold text-black dark:text-white"
                    >
                      Filter
                    </Dialog.Title>
                  </div>
                  <div>
                    <div className="border-b-2"></div>

                    {/* Grid Layout: Tabs on Left, Radio List on Right (Desktop) / Stacked (Mobile) */}
                    <div className="flex flex-col gap-4 mt-6 sm:grid sm:grid-cols-12">
                      {/* Tabs Section - Left Side on Desktop, Top on Mobile */}
                      <div className="flex flex-col gap-4 overflow-x-auto sm:overflow-x-visible sm:flex-col sm:col-span-5">
                        {!params["*"].includes("category") && (
                          <button
                            className={`w-full flex items-center justify-between gap-3 py-3 px-4 rounded-lg font-medium transition-colors text-left whitespace-nowrap ${activeTab === "categories"
                                ? "bg-[#FCEAE9] text-primary"
                                : "bg-transparent border border-[#DADADA] sm:border-none text-[#4D4C4C] dark:bg-[#1E1E1E] dark:text-white"
                              }`}
                            onClick={() => setActiveTab("categories")}
                          >
                            <div className="flex items-center gap-3">
                              <CategoryIcon className="h-[24px] w-[24px] flex-none"/>
                              <span>Categories</span>
                            </div>
                            {activeTab === "categories" ? (
                              <ChevronDownIcon className="h-5 w-5 sm:hidden flex-none" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 sm:hidden flex-none" />
                            )}
                          </button>
                        )}
                         <button
                          className={`w-full outline-none flex items-center justify-between gap-3 py-3 px-4 rounded-lg font-medium transition-colors text-left whitespace-nowrap ${activeTab === "tags"
                              ? "bg-[#FCEAE9] text-primary"
                              : "bg-transparent border border-[#DADADA] sm:border-none text-[#4D4C4C] dark:bg-[#1E1E1E] dark:text-white"
                            }`}
                          onClick={() => setActiveTab("tags")}
                        >
                          <div className="flex items-center gap-3">
                            <TagIcon className="h-[24px] w-[24px] flex-none"/>
                            <span>Tags</span>
                          </div>
                          {activeTab === "tags" ? (
                            <ChevronDownIcon className="h-5 w-5 sm:hidden flex-none" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 sm:hidden flex-none" />
                          )}
                        </button>
                      </div>

                      {/* Vertical Divider - Only visible on Desktop */}
                      <div className="hidden sm:flex sm:col-span-1 justify-center">
                        <div className="w-px bg-gray-200 dark:bg-gray-700"></div>
                      </div>

                      {/* Radio List Section - Right Side on Desktop, Bottom on Mobile */}
                      <div className="max-h-64 overflow-y-auto pb-4 sm:col-span-6">
                        {activeTab === "tags" && (
                          <div className="space-y-2">
                            {tags?.length > 0 ? (
                              tags.map((tag, i) => (
                                <label
                                  key={i}
                                  className="flex items-center p-3 rounded-lg hover:bg-[#FEECEB] dark:hover:bg-[#1E1E1E] cursor-pointer transition-colors"
                                >
                                  <input
                                    type="radio"
                                    name="tag"
                                    checked={selectedTag === tag.name}
                                    onChange={() => handleTagSelect(tag)}
                                    className="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                                  />
                                  <span className="ml-3 text-sm text-gray-900 dark:text-white capitalize">
                                    {tag.name}
                                  </span>
                                </label>
                              ))
                            ) : (
                              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                                No tags available
                              </p>
                            )}
                          </div>
                        )}

                        {activeTab === "categories" && (
                          <div className="space-y-2">
                            {props?.categories?.length > 0 ? (
                              props.categories.map((category, i) => (
                                <label
                                  key={i}
                                  className="flex items-center p-3 rounded-lg hover:bg-[#FEECEB] dark:hover:bg-[#1E1E1E] cursor-pointer transition-colors"
                                >
                                  <input
                                    type="radio"
                                    name="category"
                                    checked={selectedCategory === category.name}
                                    onChange={() => handleCategorySelect(category)}
                                    className="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                                  />
                                  <span className="ml-3 text-sm text-gray-900 dark:text-white">
                                    {category.name}
                                  </span>
                                </label>
                              ))
                            ) : (
                              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                                No categories available
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-b-2"></div>
                    <div className="flex gap-4 justify-between mt-6">
                      <button
                        className="bg-white text-primary w-48 h-12 sm:w-60 sm:mr-4 border border-primary rounded-lg"
                        type="button"
                        onClick={handleClear}
                      >
                        Clear All
                      </button>
                      <Button.Primary
                        className="bg-primary text-white w-48 h-12 sm:w-60 rounded-lg"
                        type="button"
                        title={
                          isLoading ? (
                            <Beat color={"#ffffff"} />
                          ) : (
                            "Apply Filter"
                          )
                        }
                        disabled={
                          restaurant_tags?.length +
                          restaurant_categories?.length <
                          1
                        }
                        onClick={() => {
                          props.setFilterCount(
                            restaurant_tags?.length +
                            restaurant_categories?.length
                          );
                          props.onClick(data);
                          props.onClose();
                        }}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
